import { GetWSMgr } from "@/Utils/StateLib";
import { Button, Flex, Divider, FieldText, Input } from "@theorgchartdev/storybook";
import { SourceMapComposite } from "../SourceMapComposite";
import * as React from "react";
import { enumATSModes } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSources/DataSourceSetupComposite";
import { enumFileMgrMode, kFlatFileList } from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import { DATA_SOURCES_TEST_PREFIX } from "../Utils";

export const FileBrowseComposite = ({
  file,
  directory,
  config,
  index,
  onChange,
  includeBrowse,
  includeSample,
  importType,
  chartConfig,
  orgUnitFile,
  positionFile,
  approvedPositionsFile
}) => {
  const BrowseFile = () => {
    let mode = null;
    switch (config["source"]) {
      case "Manual":
        mode = enumFileMgrMode.manual;
        break;
      case "CSV":
      case "XLS":
        mode = enumFileMgrMode.excel;
        break;
      case "SAP XML":
        mode = enumFileMgrMode.xml;
        break;
      case "SAP":
        mode = kFlatFileList.txt;
        break;
      case "UnicornHRO":
      case "OCN API":
      case "SmartChart API":
        mode = enumFileMgrMode.json;
        break;
      default:
        mode = enumFileMgrMode.importfile;
        break;
    }

    const wsMgr = GetWSMgr();
    wsMgr.DoFileMgr(mode, {
      customOnSelect: SelectFile,
      launchDirectory: directory,
      currentSelectedFile: file
    });
  };

  const SelectFile = (dirname, filename) => {
    let source = config["source"];
    if (filename) {
      const fileExtension = filename.split(".").pop().toLowerCase();
      // Determine the source based on the file type
      const isExcelFile = ["xls", "xlsx"].includes(fileExtension);
      const isCsvFile = fileExtension === "csv";

      if (isCsvFile && source.startsWith("XLS")) {
        source = "CSV"; // Change source to CSV if the selected file is CSV but current source is an Excel type
      } else if (isExcelFile && source === "CSV") {
        source = "XLS"; // Change source to XLS if the selected file is an Excel type but current source is CSV
      }
    }

    let fileKey;
    if (orgUnitFile) {
      fileKey = "orgfile";
    } else if (positionFile) {
      fileKey = "personfile";
    } else if (approvedPositionsFile) {
      fileKey = "occfile";
    } else {
      fileKey = "datafile";
    }

    // Prepare the data to be passed to onChange
    const data = {
      datadir: dirname,
      [fileKey]: filename,
      source
    };

    const oldFileIdentifier =
      config.datadir && config[fileKey] ? `${config.datadir}/${config[fileKey]}` : null;
    const newFileIdentifier = dirname && filename ? `${dirname}/${filename}` : null;
    if (oldFileIdentifier !== newFileIdentifier && newFileIdentifier !== null) {
      data.headers = null;
      data.mergeByField = undefined;
      data.mergeDupes = false;
    }

    return onChange(data, null);
  };

  const onChangeFilePath = updatedFileName => {
    const [newDirectory, newPath] = updatedFileName.split("/");
    SelectFile(newDirectory, newPath);
  };

  const combinedFilePath =
    directory && file != null ? `${directory}/${file}` : directory ? directory : (file ?? "");

  const showConfiguration = importType !== "import";
  const showMergeDupesConfig = importType !== "merge";
  const showSourceMapComposite = showConfiguration || showMergeDupesConfig;

  let firstKey = "PersonID";
  let secondKey = "SupervisorID";
  if (importType === "merge") {
    firstKey = "recordField";
    secondKey = "mergeField";
  }

  return (
    <Flex direction={"column"} gap={"12px"}>
      <Flex direction={"column"} gap={"8px"}>
        {includeBrowse ? (
          <>
            <Input
              dataTestId={`${DATA_SOURCES_TEST_PREFIX}-file-browse-filename-input`}
              value={combinedFilePath}
              onChange={e => onChangeFilePath(e.target.value)}
              placeholder={appstr("notset")}
              label={appstr("filename")}
              size="medium"
              className="ocn-form-input"
            />
            <Flex gap={"8px"}>
              <Button
                dataTestId={"file-brwsr-choose-btn"}
                onClick={BrowseFile}
                modifier={"secondary"}
                size={"medium"}
                className={"oc-special-secondary"}
              >
                {appstr("Choose file")}
              </Button>
              {includeSample && (
                <Button modifier={"secondary"} size={"medium"}>
                  {appstr("iwizselectsample")}
                </Button>
              )}
            </Flex>
          </>
        ) : (
          <>
            <FieldText
              labelSize={"medium"}
              valueSize={"large"}
              field={appstr("Directory")}
              value={directory || appstr("notset")}
            />
            <FieldText
              labelSize={"medium"}
              valueSize={"large"}
              field={appstr("filename")}
              value={file || appstr("notset")}
            />
          </>
        )}
      </Flex>
      {showSourceMapComposite && (
        <Flex direction={"column"} gap={"12px"}>
          <Divider />
          <SourceMapComposite
            chartConfig={chartConfig}
            config={config}
            importType={importType}
            index={index}
            onChange={onChange}
            idKey={firstKey}
            superIDkey={secondKey}
            ATSType={enumATSModes.toggleATS}
            showBasicConfig={showConfiguration}
            showMergeDupesConfig={showMergeDupesConfig}
            showLoadHeaders={true}
            key={combinedFilePath}
          />
        </Flex>
      )}
    </Flex>
  );
};
